/**
 * AtlasWorldModule — renders the world shell and the placed objects.
 *
 * Builds the Commons render (ground, grid, sky tint, lights) and maintains the
 * INSTANCED render representation for objects that arrive via `atlas:object-added`.
 * Object meshes are data-driven: the network module turns `world_object` rows into
 * events; this module owns the render representation and tears it down on world
 * switch. Every placed object today shares one (geometry, material) — a unit-cube
 * placeholder — so all of them batch into ONE `StaticInstancer` draw call rather than
 * one `THREE.Mesh` per row (WebGPURenderer allocates a per-render-object UBO rebound
 * every frame, so per-row Object3Ds are the CPU bottleneck at scale).
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { bus } from '../bus.js';
import { atlas } from '../state.js';
import type { PlacedObject } from '../state.js';
import { StaticInstancer } from '../lib/instancing.js';

export class AtlasWorldModule implements IModule {
  readonly name = 'AtlasWorld';

  private scene!: THREE.Scene;
  private instancer: StaticInstancer | null = null;
  private cubeGeo = new THREE.BoxGeometry(1, 1, 1);
  private cubeMat = new THREE.MeshStandardMaterial({ color: 0xffb454, roughness: 0.5, metalness: 0.05 });
  /** objectId key → StaticInstancer handles (one today — the shared cube — but keyed
   *  as an array so a future multi-submesh prefab needs no shape change here). */
  private readonly handles = new Map<string, number[]>();

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.background = new THREE.Color(0x0b0e14);
    this.instancer = new StaticInstancer(this.scene, 4096, 'world-objects');
    this.instancer.setRainLayer(true); // world objects shelter (render wave 1)

    // Lighting is owned by LightingModule (sun arc + ambient, band-profile driven,
    // day/night). The constant HemisphereLight(1.0) + DirectionalLight(2.4) this
    // module used to add stacked ON TOP of those and floodlit the world at
    // midnight — night could never get dark. Removed; no lights live here.

    // (No placeholder ground/grid — EnvironmentModule renders the real terrain:
    // streamed biome chunks in the Commons, a heightfield in personal worlds.)

    // Object lifecycle.
    bus.on('atlas:object-added', (o) => this.addObject(o));
    bus.on('atlas:object-removed', ({ objectId }) => this.removeObject(objectId));
    bus.on('atlas:world-cleared', () => this.clearObjects());
  }

  private addObject(o: {
    objectId: bigint;
    prefabId: string;
    x: number;
    y: number;
    z: number;
    rotX: number;
    rotY: number;
    rotZ: number;
    rotW: number;
    scale: number;
  }): void {
    const key = o.objectId.toString();
    if (atlas.worldObjects.has(key)) return;

    if (this.instancer) {
      const matrix = new THREE.Matrix4().compose(
        new THREE.Vector3(o.x, o.y, o.z),
        new THREE.Quaternion(o.rotX, o.rotY, o.rotZ, o.rotW),
        new THREE.Vector3(o.scale, o.scale, o.scale),
      );
      const id = this.instancer.add(this.cubeGeo, this.cubeMat, matrix);
      if (id >= 0) this.handles.set(key, [id]);
    }

    const placed: PlacedObject = {
      objectId: o.objectId,
      prefabId: o.prefabId,
      x: o.x,
      y: o.y,
      z: o.z,
      rotX: o.rotX,
      rotY: o.rotY,
      rotZ: o.rotZ,
      rotW: o.rotW,
      scale: o.scale,
      mesh: null, // render representation is instanced now — no per-row Object3D to hand back
    };
    atlas.worldObjects.set(key, placed);
  }

  private removeObject(objectId: bigint): void {
    const key = objectId.toString();
    this.removeHandles(key);
    atlas.worldObjects.delete(key);
  }

  private clearObjects(): void {
    for (const key of atlas.worldObjects.keys()) this.removeHandles(key);
    atlas.worldObjects.clear();
  }

  /** Remove every StaticInstancer handle contributed by objectId `key`, if any. */
  private removeHandles(key: string): void {
    const ids = this.handles.get(key);
    if (!ids) return;
    for (const id of ids) this.instancer?.remove(id);
    this.handles.delete(key);
  }

  dispose(): void {
    this.clearObjects();
    this.instancer?.dispose();
    this.cubeGeo.dispose();
    this.cubeMat.dispose();
  }
}
